import type { BalanceConfig, CombatTuning, EnemyConfig, SkillConfig } from "@bastion/data";
import type { ResolvedMap } from "../repos/ConfigRepository";
import type {
  ControlType,
  DamageType,
  EnemyState,
  GameState,
  GameEvent,
  Point,
  Random,
  TowerState,
} from "../types";
import { calculateDamage, calculateControlChance, calculateControlModifier } from "./damage";
import { calculateMonsterGoldReward } from "./rewards";
import type { CombatResult } from "./tower";
import { createBaseDistanceField } from "../wave/movement";
import type { BaseDistanceField } from "../wave/movement";
import { scaleEnemyStats } from "../wave/scaling";

/** 将 EnemyState 及其配置适配为 calculateDamage 期望的目标视图。 */
export function targetView(
  enemy: EnemyState,
  enemyConfig: EnemyConfig,
): { defense: number; magicResistance: number; hp: number } {
  return {
    defense: enemy.defense ?? enemyConfig.stats.defense,
    magicResistance: enemy.magicResistance ?? enemyConfig.stats.magicResistance,
    hp: enemy.hp,
  };
}

/** 获取敌人的抗性数值，用于计算控制命中概率。 */
export function getTargetResistance(enemyConfig: EnemyConfig): number {
  return enemyConfig.stats?.resistance ?? 0;
}

export interface CombatSystemDeps {
  resolvedMap: ResolvedMap;
  skills?: Record<string, SkillConfig>;
  balance: BalanceConfig;
  random: Random;
  tuning?: CombatTuning;
  /** 引擎阶段注入的本 Tick 落地轨道打击；伤害并入统一死亡/奖励阶段。 */
  orbitalStrikes?: Array<{ id: string; position: Point; damage?: number }>;
  /** 按当前地图和炮台占用计算的基地距离场。 */
  baseDistanceField?: BaseDistanceField;
  /** 引擎在独立阶段运行状态 tick；直接调用者保持默认值即可。 */
  skipStatusTick?: boolean;
}

function withBaseDistanceField(state: GameState, deps: CombatSystemDeps): CombatSystemDeps {
  if (deps.baseDistanceField) return deps;
  return {
    ...deps,
    baseDistanceField: createBaseDistanceField(
      state.towers.map((tower) => tower.position),
      deps.resolvedMap,
    ),
  };
}

const COMPATIBILITY_TUNING: CombatTuning = {
  id: "combat-compatibility-default",
  splashDamageMultiplier: 0.5,
  chainDamageMultiplier: 0.5,
  towerAutoSkill: { damageMultiplier: 2, attackSpeedFactor: 2, attackSpeedDuration: 3 },
};

function getCombatTuning(deps: CombatSystemDeps): CombatTuning {
  return deps.tuning ?? COMPATIBILITY_TUNING;
}

/**
 * 编排一次战斗 tick：推进所有塔、发射投射物、移动投射物、解析命中、
 * 应用持续伤害、处理死亡和奖励。
 *
 * 同时驱动：
 * - 炮台技能按 releaseCondition 触发：attack_count 仅在成功发射投射物时递增。
 * - 怪物技能按关联技能的 interval 条件计时，释放后重置为完整间隔。
 * - 战斗管线事件（§34）累积在 result.events 上，由引擎盖章并发出。
 */
/**
 * 编排一次完整的战斗 tick：攻击/技能阶段（不含投射物移动、状态 tick、
 * 再生或死亡）。引擎的 Phase 4 直接使用此方法；runCombatTick 保留给
 * 希望在单次调用中完成完整管线的单元测试/集成测试使用。
 */
export function runAttackSkillPhase(
  state: GameState,
  dt: number,
  deps: CombatSystemDeps,
  result: CombatTickResult,
): void {
  deps = withBaseDistanceField(state, deps);
  for (const tower of state.towers) {
    const combatResult = tickTowerFor(tower, state, dt, deps, result);
    if (combatResult.projectile) {
      state.projectiles.push(combatResult.projectile);
      state.entityCounter += 1;
      result.newProjectiles.push(combatResult.projectile);
    }
    applyDotTicks(state, combatResult, result);
    if (combatResult.hits.length > 0 && !hasDodged(combatResult)) {
      applyChainAttacks(state, combatResult, result, tower, deps);
    }
    for (const splashHit of combatResult.splash) {
      const splashEnemy = state.enemies.find((e) => e.id === splashHit.enemyId);
      if (!splashEnemy || !splashEnemy.alive) continue;
      applyDamageTo(
        state,
        splashEnemy.id,
        splashHit.damage,
        splashHit.damageType,
        false,
        result,
        tower.id,
        "tower",
      );
    }
  }
  tickActiveDots(state, dt, result);
  tickMonsterSkills(state, dt, deps, result);
}

/**
 * Orchestrates one full combat tick: attack/skill, projectile flight,
 * damage, status effects, regen, and death processing.
 * Used by unit tests and integration tests. For the engine's phased
 * tick, use the individual phase methods instead.
 */
export function runCombatTick(
  state: GameState,
  dt: number,
  deps: CombatSystemDeps,
): CombatTickResult {
  const result: CombatTickResult = {
    damageEvents: [],
    killEvents: [],
    newProjectiles: [],
    events: [],
    landedOrbitalStrikes: [],
    killedThisTick: new Set<string>(),
  };
  deps = withBaseDistanceField(state, deps);

  // Step 1: 为每个塔选择目标、发射投射物并应用效果。

  for (const tower of state.towers) {
    const combatResult = tickTowerFor(tower, state, dt, deps, result);
    if (combatResult.projectile) {
      state.projectiles.push(combatResult.projectile);
      state.entityCounter += 1;
      result.newProjectiles.push(combatResult.projectile);
    }
    applyDotTicks(state, combatResult, result);
    // 连锁攻击：向范围内附近的敌人发射额外弹矢。
    // 当塔的 chainCount > 0 时，对主要目标附近的敌人造成伤害。
    if (combatResult.hits.length > 0 && !hasDodged(combatResult)) {
      applyChainAttacks(state, combatResult, result, tower, deps);
    }
    // 溅射伤害：对主命中周围附近的敌人施加范围伤害。
    for (const splashHit of combatResult.splash) {
      const splashEnemy = state.enemies.find((e) => e.id === splashHit.enemyId);
      if (!splashEnemy || !splashEnemy.alive) continue;
      applyDamageTo(
        state,
        splashEnemy.id,
        splashHit.damage,
        splashHit.damageType,
        false,
        result,
        tower.id,
        "tower",
      );
    }
  }

  // 持久的 DoT 每个模拟 tick 执行一次，独立于攻击冷却。
  tickActiveDots(state, dt, result);
  tickMonsterSkills(state, dt, deps, result);
  moveProjectiles(state, dt, result, deps);
  for (const strike of deps.orbitalStrikes ?? []) {
    const skill = deps.skills?.["area_damage"];
    if (!skill) continue;
    const applied = applySkillDamage(state, skill, strike.position, deps, {
      deferDeathProcessing: true,
      damageOverride: strike.damage ?? skill.damage ?? 300,
    });
    result.damageEvents.push(...applied.damageEvents);
    result.events.push(...applied.events);
    result.landedOrbitalStrikes.push(strike);
  }
  applyRegen(state, dt, deps);
  processDeaths(state, result, deps);

  // Step 6: 在所有伤害/死亡解析完毕后，再 tick 状态效果。
  // 这样确保已过期的效果不会影响本次 tick 的移动（移动已在战斗前运行），
  // 但本次 tick 攻击产生的新效果会在下次 tick 移动前正确过期。
  if (!deps.skipStatusTick) tickStatusEffects(state, dt, result);
  // 衰减塔的攻击速度增益（与 statusEffects 同步推进）。
  if (!deps.skipStatusTick) tickAttackSpeedBoosts(state, dt);
  return result;
}

/** 衰减所有塔的攻击速度增益。每个 boost 的 remaining 递减，到期后移除。 */
export function tickAttackSpeedBoosts(state: GameState, dt: number): void {
  for (const tower of state.towers) {
    for (let i = tower.attackSpeedBoosts.length - 1; i >= 0; i--) {
      tower.attackSpeedBoosts[i].remaining -= dt;
      if (tower.attackSpeedBoosts[i].remaining <= 0) {
        tower.attackSpeedBoosts.splice(i, 1);
      }
    }
  }
}

/**
 * 怪物周期性技能：递减每个怪物的技能计时器，归零时尝试释放，随后重置为完整间隔。
 */
function tickMonsterSkills(
  state: GameState,
  dt: number,
  deps: CombatSystemDeps,
  result: CombatTickResult,
): void {
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const config = deps.resolvedMap.enemies[enemy.type];
    if (!config) continue;
    for (const skillId of config.skillIds ?? []) {
      const skill = deps.skills?.[skillId];
      const condition = skill?.releaseCondition;
      if (skill?.type !== "MONSTER" || condition?.kind !== "interval") continue;
      const timers = (enemy.skillTimers ??= {});
      const remaining = (timers[skillId] ?? condition.initialDelay ?? condition.seconds) - dt;
      if (remaining > 0) {
        timers[skillId] = remaining;
        continue;
      }
      timers[skillId] = condition.seconds;
      if (!castMonsterSkill(state, enemy, skill, result)) continue;
      result.events.push({
        type: "skill_cast",
        time: 0,
        payload: { sourceId: enemy.id, sourceKind: "enemy", skillId },
      });
    }
  }
}

/** 对范围内其他存活怪物施加怪物技能的减速效果。 */
function castMonsterSkill(
  state: GameState,
  caster: EnemyState,
  skill: SkillConfig,
  _result?: CombatTickResult,
): boolean {
  const radius = skill.radius ?? 60;
  const slowFactor = skill.slowFactor ?? 0.75;
  const duration = skill.slowDuration ?? 3;
  const targets = state.enemies.filter(
    (ally) =>
      ally.alive &&
      !ally.markedDead &&
      ally.id !== caster.id &&
      Math.hypot(ally.position.x - caster.position.x, ally.position.y - caster.position.y) <=
        radius,
  );
  if (targets.length === 0) return false;
  for (const ally of targets) applySlowTo(ally, slowFactor, duration);
  return true;
}

/** 应用/刷新减速状态效果（规则 §20：刷新，不叠加）。 */
function applySlowTo(enemy: EnemyState, factor: number, duration: number): void {
  const existing = enemy.statusEffects.find((e) => e.key === "slow");
  if (existing && existing.remaining >= duration && (existing.factor ?? 1) <= factor) return;
  if (existing) {
    existing.remaining = duration;
    existing.factor = factor;
  } else {
    enemy.statusEffects.push({
      key: "slow",
      remaining: duration,
      modifiers: [],
      controlType: "slow",
      factor,
    });
  }
}

/**
 * 通过命中检定 vs 抗性来判断控制是否生效。
 * 控制生效返回 true，被抵抗返回 false。
 * 通过 result 发出 control_applied 或 control_resisted 事件。
 */
export function applyControl(
  enemy: EnemyState,
  enemyConfig: EnemyConfig,
  type: ControlType,
  baseChance: number,
  sourceAccuracy: number,
  duration: number,
  factor: number,
  random: Random,
  result: Pick<CombatTickResult, "events">,
): boolean {
  const resistance = getTargetResistance(enemyConfig);
  const chance = calculateControlChance(baseChance, sourceAccuracy, resistance);
  const roll = random.next();
  if (roll < chance) {
    // §2.1: Accuracy/Resistance modifies both effect strength and duration.
    const modifier = calculateControlModifier(sourceAccuracy, resistance);
    const finalFactor = factor * modifier;
    const finalDuration = duration * modifier;
    applyControlTo(enemy, type, finalDuration, finalFactor);
    result.events.push({
      type: "control_applied",
      time: 0,
      payload: { type, targetId: enemy.id, duration: finalDuration, factor: finalFactor },
    });
    return true;
  }
  result.events.push({
    type: "control_resisted",
    time: 0,
    payload: { type, targetId: enemy.id, resistance, chance },
  });
  return false;
}

/** 根据类型对敌人施加控制效果（规则 §19, §20）。 */
function applyControlTo(
  enemy: EnemyState,
  type: ControlType,
  duration: number,
  factor: number,
): void {
  if (type === "slow") {
    applySlowTo(enemy, factor, duration);
  } else if (type === "stun") {
    const existing = enemy.statusEffects.find(
      (effect) => effect.key === "stun" && effect.source == null,
    );
    if (!existing || existing.remaining < duration) {
      if (existing) {
        existing.remaining = duration;
      } else {
        enemy.statusEffects.push({
          key: "stun",
          remaining: duration,
          modifiers: [],
          controlType: "stun",
        });
      }
    }
  } else if (type === "root") {
    // 根植目前与眩晕类似（禁止移动）
    const existing = enemy.statusEffects.find(
      (effect) => effect.key === "root" && effect.source == null,
    );
    if (!existing || existing.remaining < duration) {
      if (existing) {
        existing.remaining = duration;
      } else {
        enemy.statusEffects.push({
          key: "root",
          remaining: duration,
          modifiers: [],
          controlType: "root",
        });
      }
    }
  }
  // silence: not implemented in phase 1
}

/** 如果战斗结果中的所有命中都造成零伤害（闪避），则返回 true。 */
function hasDodged(cr: CombatResult): boolean {
  return cr.hits.length > 0 && cr.hits.every((h) => h.damage <= 0);
}

function upgradeAttack(config: ResolvedMap["towers"][string], level: number): number {
  const upgrade = config.upgrades[level - 1] ?? config.upgrades[0];
  return upgrade.attack ?? config.stats.attack;
}

function upgradeValue(
  config: ResolvedMap["towers"][string],
  level: number,
  key: "attackSpeed" | "range" | "splashRadius" | "chainCount",
): number {
  const upgrade = config.upgrades[level - 1] ?? config.upgrades[0];
  if (key === "attackSpeed") return upgrade.attackSpeed ?? config.attackSpeed;
  if (key === "range") return upgrade.range ?? config.range;
  if (key === "splashRadius")
    return (upgrade as { splashRadius?: number }).splashRadius ?? config.splashRadius ?? 0;
  if (key === "chainCount")
    return (upgrade as { chainCount?: number }).chainCount ?? config.chainCount ?? 0;
  return 0;
}

/**
 * 计算单个塔在当前时刻的攻击计划。
 * 处理：目标选择、伤害计算、闪避判定、持续伤害、减速、
 * 溅射半径，以及记录命中（或闪避）。
 */
function tickTowerFor(
  tower: TowerState,
  state: GameState,
  dt: number,
  deps: CombatSystemDeps,
  result: CombatTickResult,
): CombatResult {
  const config = deps.resolvedMap.towers[tower.type];
  if (!config) throw new Error(`tickTowerFor: unknown tower type "${tower.type}"`);

  const out: CombatResult = { projectile: null, hits: [], splash: [], dots: [] };
  const skills = (config.skillIds ?? [])
    .map((skillId) => deps.skills?.[skillId])
    .filter((skill): skill is SkillConfig => skill?.type === "TOWER");
  const hasActiveBoost = tower.attackSpeedBoosts.some((boost) => boost.remaining > 0);
  const activeBoostFactor = tower.attackSpeedBoosts.reduce(
    (factor, boost) => factor * boost.factor,
    1,
  );
  const baseCooldownDuration = 1 / upgradeValue(config, tower.level, "attackSpeed");
  const cooldownDuration = baseCooldownDuration / activeBoostFactor;
  const attempted = new Set<string>();

  // 普通自动技能逐个尝试；NextAttack 技能按 skillIds 顺序争用唯一替换/强化槽。
  let pendingSkill = tower.pendingAttackSkillId
    ? skills.find(
        (skill) => skill.id === tower.pendingAttackSkillId && skill.nextAttack !== undefined,
      )
    : undefined;
  if (tower.level >= (config.maxLevel ?? 1)) {
    for (const skill of skills) {
      const condition = skill.releaseCondition;
      if (
        condition?.kind !== "attack_count" ||
        (tower.skillReleaseProgress[skill.id] ?? 0) < condition.count
      )
        continue;
      if (skill.nextAttack) {
        if (!pendingSkill) pendingSkill = skill;
        continue;
      }
      if (hasActiveBoost) continue;
      attempted.add(skill.id);
      if (castTowerSkill(state, tower, config, skill, deps, result)) {
        tower.skillReleaseProgress[skill.id] = 0;
        tower.cooldown = cooldownDuration;
      }
    }
  }
  if (pendingSkill && !tower.pendingAttackSkillId) tower.pendingAttackSkillId = pendingSkill.id;

  if (config.attackMode === "continuous_laser") {
    return tickContinuousLaser(tower, state, dt, deps, result, config, cooldownDuration);
  }

  if (tower.laserTargetId !== undefined || tower.laserPulseCount !== undefined) {
    tower.laserTargetId = undefined;
    tower.laserPulseCount = 0;
  }
  tower.cooldown -= dt;
  if (tower.cooldown > 0) return out;

  const range = upgradeValue(config, tower.level, "range");
  const targetId = selectTargetSafe(tower.position, range, state.enemies, deps.baseDistanceField);
  if (!targetId) {
    tower.cooldown = 0;
    return out;
  }
  const target = state.enemies.find((enemy) => enemy.id === targetId);
  if (!target || !target.alive) {
    tower.cooldown = cooldownDuration;
    return out;
  }
  const targetConfig = deps.resolvedMap.enemies[target.type];
  if (!targetConfig) {
    tower.cooldown = cooldownDuration;
    return out;
  }

  const damageResult = calculateDamage(config, tower.level, targetView(target, targetConfig), {
    random: deps.random,
    balance: deps.balance,
  });
  let actualDamage = damageResult.amount;
  const pendingAttackSkill = tower.pendingAttackSkillId
    ? skills.find(
        (skill) => skill.id === tower.pendingAttackSkillId && skill.nextAttack !== undefined,
      )
    : undefined;
  const nextAttack = pendingAttackSkill?.nextAttack;
  const areaDamage = nextAttack?.kind === "area_damage" ? nextAttack : undefined;
  const iceCone = nextAttack?.kind === "ice_cone" ? nextAttack : undefined;
  const isNextAttack = nextAttack !== undefined;

  if (areaDamage) {
    actualDamage = 0;
  } else {
    if (
      targetConfig.dodgeChance &&
      targetConfig.dodgeChance > 0 &&
      deps.random.next() < targetConfig.dodgeChance
    ) {
      actualDamage = 0;
    }
    if (iceCone) actualDamage *= iceCone.damageMultiplier;
  }

  const hitCritical = damageResult.isCritical;
  const distance = Math.hypot(
    target.position.x - tower.position.x,
    target.position.y - tower.position.y,
  );
  const baseAttack = upgradeAttack(config, tower.level);
  out.projectile = {
    id: `proj-${state.entityCounter}`,
    sourceTowerId: tower.id,
    targetEnemyId: target.id,
    position: { ...tower.position },
    speed: iceCone
      ? config.projectileSpeed * iceCone.projectileSpeedMultiplier
      : config.projectileSpeed,
    damage: actualDamage,
    damageType: areaDamage ? "physical" : damageResult.damageType,
    attackMode: areaDamage ? "area_damage" : iceCone ? "ice_cone" : "normal",
    impactControl: iceCone
      ? {
          radius: iceCone.impactRadiusCells * deps.resolvedMap.map.gridSize,
          slowFactor: iceCone.slowFactor,
          slowDuration: iceCone.slowDuration,
          controlChance: iceCone.controlChance,
          sourceAccuracy: config.stats.accuracy,
        }
      : undefined,
    impactAreaDamage: areaDamage
      ? {
          radius: areaDamage.radius,
          baseAttack,
          targetDamageMultiplier: areaDamage.targetDamageMultiplier,
          otherDamageMultiplier: areaDamage.otherDamageMultiplier,
          globalDamageMultiplier: deps.balance.globalDamageMultiplier ?? 1,
          isCritical: hitCritical,
          critDamage: config.stats.critDamage,
        }
      : undefined,
    flightTime: config.projectileSpeed > 0 ? distance / config.projectileSpeed : 0,
    resolved: false,
  };
  out.hits.push({
    enemyId: target.id,
    damage: areaDamage ? damageResult.amount : actualDamage,
    damageType: areaDamage ? "physical" : damageResult.damageType,
    isCritical: areaDamage ? hitCritical : damageResult.isCritical,
  });

  if (pendingAttackSkill && nextAttack) {
    tower.pendingAttackSkillId = undefined;
    tower.skillReleaseProgress[pendingAttackSkill.id] = 0;
    result.events.push({
      type: "skill_cast",
      time: 0,
      payload: { sourceId: tower.id, sourceKind: "tower", skillId: pendingAttackSkill.id },
    });
  }
  if (tower.type === "thunder_tower") {
    result.events.push({
      type: "lightning_strike",
      time: 0,
      payload: {
        sourceId: tower.id,
        origin: { ...tower.position },
        target: { ...target.position },
      },
    });
  }
  result.events.push({
    type: "attack_started",
    time: 0,
    payload: { sourceId: tower.id, sourceKind: "tower", targetId: target.id },
  });
  if (areaDamage ? hitCritical : damageResult.isCritical) {
    result.events.push({
      type: "critical_hit",
      time: 0,
      payload: {
        sourceId: tower.id,
        sourceKind: "tower",
        targetId: target.id,
        damage: actualDamage,
        critDamage: config.stats.critDamage,
      },
    });
  }

  if (!isNextAttack && !hasActiveBoost && tower.level >= (config.maxLevel ?? 1)) {
    for (const skill of skills) {
      const condition = skill.releaseCondition;
      if (condition?.kind !== "attack_count" || attempted.has(skill.id)) continue;
      const progress = tower.skillReleaseProgress[skill.id] ?? 0;
      if (progress >= condition.count) continue;
      const ready = Math.min(condition.count, progress + 1);
      tower.skillReleaseProgress[skill.id] = ready;
      if (ready < condition.count) continue;
      if (skill.nextAttack) {
        if (!tower.pendingAttackSkillId) tower.pendingAttackSkillId = skill.id;
      } else if (castTowerSkill(state, tower, config, skill, deps, result)) {
        tower.skillReleaseProgress[skill.id] = 0;
        tower.cooldown = cooldownDuration;
      }
    }
  }

  if (!areaDamage && config.dotOnHit)
    out.dots.push({
      enemyId: target.id,
      dps: config.dotOnHit.dps,
      damageType: config.dotOnHit.damageType,
      duration: config.dotOnHit.duration,
      sourceId: tower.id,
    });
  const tuning = getCombatTuning(deps);
  const splashRadius = upgradeValue(config, tower.level, "splashRadius");
  if (splashRadius > 0) {
    for (const enemy of state.enemies) {
      if (!enemy.alive || enemy.id === target.id) continue;
      if (
        Math.hypot(enemy.position.x - target.position.x, enemy.position.y - target.position.y) <=
        splashRadius
      ) {
        out.splash.push({
          enemyId: enemy.id,
          damage: actualDamage * tuning.splashDamageMultiplier,
          damageType: damageResult.damageType,
        });
      }
    }
  }
  tower.cooldown = cooldownDuration;
  return out;
}

function tickContinuousLaser(
  tower: TowerState,
  state: GameState,
  dt: number,
  deps: CombatSystemDeps,
  result: CombatTickResult,
  config: ResolvedMap["towers"][string],
  cooldownDuration: number,
): CombatResult {
  const out: CombatResult = { projectile: null, hits: [], splash: [], dots: [] };
  const range = upgradeValue(config, tower.level, "range");
  const targetId = selectTargetSafe(tower.position, range, state.enemies, deps.baseDistanceField);

  if (targetId !== tower.laserTargetId) {
    tower.laserTargetId = targetId ?? undefined;
    tower.laserPulseCount = 0;
  }
  if (!targetId) {
    tower.laserPulseCount = 0;
    tower.cooldown = 0;
    return out;
  }

  tower.cooldown -= dt;
  if (tower.cooldown > 1e-9) return out;

  const target = state.enemies.find((enemy) => enemy.id === targetId);
  if (!target || !target.alive || target.markedDead || target.hp <= 0) {
    tower.laserTargetId = undefined;
    tower.laserPulseCount = 0;
    tower.cooldown = 0;
    return out;
  }
  const targetConfig = deps.resolvedMap.enemies[target.type];
  if (!targetConfig) {
    tower.laserTargetId = undefined;
    tower.laserPulseCount = 0;
    tower.cooldown = 0;
    return out;
  }

  const enhancement = config.laserEnhancement;
  const isMaxLevel = tower.level >= config.maxLevel;
  const isEnhanced =
    isMaxLevel && (tower.laserPulseCount ?? 0) >= (enhancement?.pulseCount ?? Infinity);
  const damageResult = calculateDamage(config, tower.level, targetView(target, targetConfig), {
    random: deps.random,
    balance: deps.balance,
    damageMultiplier: isEnhanced ? (enhancement?.damageMultiplier ?? 1) : 1,
  });
  if (isMaxLevel) tower.laserPulseCount = (tower.laserPulseCount ?? 0) + 1;
  tower.cooldown = cooldownDuration;
  out.projectile = {
    id: `proj-${state.entityCounter}`,
    sourceTowerId: tower.id,
    targetEnemyId: target.id,
    position: { ...target.position },
    speed: 0,
    damage: damageResult.amount,
    damageType: damageResult.damageType,
    attackMode: "continuous_laser",
    isCritical: damageResult.isCritical,
    flightTime: 0,
    resolved: false,
  };
  result.events.push({
    type: "attack_started",
    time: 0,
    payload: {
      sourceId: tower.id,
      sourceKind: "tower",
      targetId: target.id,
      attackMode: "continuous_laser",
      enhanced: isEnhanced,
    },
  });
  if (damageResult.isCritical) {
    result.events.push({
      type: "critical_hit",
      time: 0,
      payload: {
        sourceId: tower.id,
        sourceKind: "tower",
        targetId: target.id,
        damage: damageResult.amount,
        critDamage: config.stats.critDamage,
      },
    });
  }
  return out;
}

function selectTargetSafe(
  position: { x: number; y: number },
  range: number,
  enemies: EnemyState[],
  baseDistanceField: BaseDistanceField | undefined,
): string | null {
  const candidates = enemies
    .map((enemy, order) => ({ enemy, order }))
    .filter(
      ({ enemy }) =>
        enemy.alive &&
        !enemy.markedDead &&
        enemy.hp > 0 &&
        Math.hypot(enemy.position.x - position.x, enemy.position.y - position.y) <= range,
    );
  if (candidates.length === 0) return null;

  let best: { enemy: EnemyState; order: number; distance: number } | undefined;
  for (const { enemy, order } of candidates) {
    const distance = enemy.debugStationary
      ? Math.hypot(enemy.position.x - position.x, enemy.position.y - position.y)
      : (baseDistanceField?.distanceAt(enemy.position) ?? Infinity);
    if (!best || distance < best.distance || (distance === best.distance && order < best.order)) {
      best = { enemy, order, distance };
    }
  }
  return best?.enemy.id ?? null;
}

/**
 * 当充能满时自动施放塔技能（规则 §25）。
 * 阶段-1：范围伤害技能，命中半径内所有敌人。
 */
function castTowerSkill(
  state: GameState,
  tower: TowerState,
  config: import("@bastion/data").TowerConfig,
  towerSkill: SkillConfig,
  deps: CombatSystemDeps,
  result: CombatTickResult,
): boolean {
  if (towerSkill.type !== "TOWER" || tower.level < (config.maxLevel ?? 1)) return false;
  if (towerSkill.attackSpeedFactor != null && towerSkill.attackSpeedDuration != null) {
    result.events.push({
      type: "skill_cast",
      time: 0,
      payload: { sourceId: tower.id, sourceKind: "tower", skillId: towerSkill.id },
    });
    tower.attackSpeedBoosts.push({
      factor: towerSkill.attackSpeedFactor,
      remaining: towerSkill.attackSpeedDuration,
      skillId: towerSkill.id,
    });
    return true;
  }

  const autoSkill = towerSkill.autoSkill;
  if (!autoSkill) return false;
  const targets = state.enemies.filter(
    (enemy) =>
      enemy.alive &&
      !enemy.markedDead &&
      Math.hypot(enemy.position.x - tower.position.x, enemy.position.y - tower.position.y) <=
        autoSkill.radius,
  );
  if (targets.length === 0) return false;
  result.events.push({
    type: "skill_cast",
    time: 0,
    payload: { sourceId: tower.id, sourceKind: "tower", skillId: towerSkill.id },
  });
  for (const enemy of targets) {
    const enemyConfig = deps.resolvedMap.enemies[enemy.type];
    if (!enemyConfig) continue;
    const dmgResult = calculateDamage(config, tower.level, targetView(enemy, enemyConfig), {
      random: deps.random,
      balance: deps.balance,
      damageMultiplier: autoSkill.damageMultiplier,
      damageTypeOverride: towerSkill.damageType,
    });
    applyDamageTo(
      state,
      enemy.id,
      dmgResult.amount,
      dmgResult.damageType,
      dmgResult.isCritical,
      result,
      tower.id,
      "tower",
    );
  }
  return true;
}

function applyDotTicks(
  state: GameState,
  combatResult: CombatResult,
  result: CombatTickResult,
): void {
  for (const dot of combatResult.dots) {
    const enemy = state.enemies.find((e) => e.id === dot.enemyId);
    if (!enemy || !enemy.alive) continue;

    let dotEffect = enemy.statusEffects.find((e) => e.key === "dot" && e.source === dot.sourceId);
    if (!dotEffect) {
      dotEffect = {
        key: "dot",
        remaining: dot.duration,
        modifiers: [{ stat: "dps", mode: "FLAT", value: dot.dps, duration: dot.duration }],
        source: dot.sourceId,
        damageType: dot.damageType,
      };
      enemy.statusEffects.push(dotEffect);
    } else if (dot.duration > dotEffect.remaining) {
      dotEffect.remaining = dot.duration;
      dotEffect.modifiers = [{ stat: "dps", mode: "FLAT", value: dot.dps, duration: dot.duration }];
      dotEffect.damageType = dot.damageType;
    }

    // DoT 规则："DoT 在施加瞬间立即造成第一次伤害"——立即施加第一个 tick 伤害。
    // 使用 dt=0 使得只计算剩余持续时间的伤害（而非整 tick）。
    tickSingleDot(state, enemy, dotEffect, 0, result, dot.damageType ?? "magic");
  }
}

/** 每个模拟 tick 对持久的 DoT 进行一次 tick，独立于攻击冷却。 */
export function tickActiveDots(state: GameState, dt: number, result: CombatTickResult): void {
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const dots = enemy.statusEffects.filter((effect) => effect.key === "dot");
    for (const dot of dots) {
      tickSingleDot(state, enemy, dot, dt, result, dot.damageType ?? "magic");
    }
  }
}

function tickSingleDot(
  state: GameState,
  enemy: EnemyState,
  effect: import("../types").StatusEffect,
  dt: number,
  result: CombatTickResult,
  damageType: DamageType,
): void {
  const dps = effect.modifiers.find((modifier) => modifier.stat === "dps")?.value ?? 0;
  const effectiveDt = Math.min(dt, Math.max(0, effect.remaining));
  if (dps > 0 && effectiveDt > 0) {
    applyDamageTo(
      state,
      enemy.id,
      dps * effectiveDt,
      damageType,
      false,
      result,
      effect.source,
      "tower",
    );
  }
  effect.remaining -= dt;
  if (effect.remaining <= 0) {
    const index = enemy.statusEffects.indexOf(effect);
    if (index >= 0) enemy.statusEffects.splice(index, 1);
  }
}

function applyDamageTo(
  state: GameState,
  enemyId: string,
  damage: number,
  damageType: DamageType,
  isCritical: boolean,
  result: CombatTickResult,
  sourceId?: string,
  sourceKind?: "tower" | "enemy" | "skill" | "environment",
): void {
  const enemy = state.enemies.find((e) => e.id === enemyId);
  if (!enemy || !enemy.alive) return;
  enemy.hp = Math.max(0, enemy.hp - damage);
  result.damageEvents.push({
    enemyId,
    damage,
    damageType,
    isCritical,
    hpRemaining: enemy.hp,
    sourceId,
    sourceKind,
  });
  if (enemy.hp <= 0) {
    enemy.markedDead = true;
    // 不要在此处设置 alive = false——那是在死亡阶段处理（§3.9）。
  }
}

/**
 * Tick all active status effects: decrement duration, remove expired ones,
 * and fire corresponding events. Prevents negative duration and double-removal.
 */
export function tickStatusEffects(state: GameState, dt: number, _result?: CombatTickResult): void {
  for (let i = state.enemies.length - 1; i >= 0; i--) {
    const enemy = state.enemies[i];
    if (!enemy.alive) continue;
    for (let j = enemy.statusEffects.length - 1; j >= 0; j--) {
      const effect = enemy.statusEffects[j];
      // 持续伤害由其 tickSingleDot 管理持续时间和伤害 tick。
      if (effect.key === "dot") continue;
      effect.remaining -= dt;
      if (effect.remaining <= 0) {
        enemy.statusEffects.splice(j, 1);
      } else {
        effect.remaining = Math.max(0, effect.remaining);
      }
    }
  }
}

/**
 * 从普通攻击主目标开始逐跳选择最近的未命中敌人。
 * chainCount 表示主目标之外的额外跳数；后续每跳均以当前敌人为圆心使用本级 range。
 */
function applyChainAttacks(
  state: GameState,
  combatResult: CombatResult,
  result: CombatTickResult,
  tower: TowerState,
  deps: CombatSystemDeps,
): void {
  const config = deps.resolvedMap.towers[tower.type];
  if (!config) return;
  const chainCount = upgradeValue(config, tower.level, "chainCount");
  if (chainCount <= 0) return;
  const range = upgradeValue(config, tower.level, "range");
  const primaryTargetId = combatResult.hits[0]?.enemyId;
  const primaryTarget = primaryTargetId
    ? state.enemies.find((enemy) => enemy.id === primaryTargetId)
    : undefined;
  if (!primaryTarget) return;

  const tuning = getCombatTuning(deps);
  const visited = new Set([primaryTarget.id]);
  const chainedTargets: EnemyState[] = [];
  let current = primaryTarget;

  for (let hop = 0; hop < chainCount; hop++) {
    const candidates = state.enemies
      .map((enemy, order) => ({ enemy, order }))
      .filter(({ enemy }) => {
        if (!enemy.alive || enemy.markedDead || visited.has(enemy.id)) return false;
        return (
          Math.hypot(
            enemy.position.x - current.position.x,
            enemy.position.y - current.position.y,
          ) <= range
        );
      })
      .map(({ enemy, order }) => ({
        enemy,
        order,
        jumpDistance: Math.hypot(
          enemy.position.x - current.position.x,
          enemy.position.y - current.position.y,
        ),
        baseDistance: deps.baseDistanceField?.distanceAt(enemy.position) ?? Infinity,
      }))
      .sort((a, b) => {
        if (a.jumpDistance !== b.jumpDistance) return a.jumpDistance - b.jumpDistance;
        if (a.baseDistance !== b.baseDistance) return a.baseDistance - b.baseDistance;
        return a.order - b.order;
      });
    const next = candidates[0]?.enemy;
    if (!next) break;

    visited.add(next.id);
    chainedTargets.push(next);
    const enemyConfig = deps.resolvedMap.enemies[next.type];
    if (enemyConfig) {
      const damageResult = calculateDamage(config, tower.level, targetView(next, enemyConfig), {
        random: deps.random,
        balance: deps.balance,
      });
      applyDamageTo(
        state,
        next.id,
        damageResult.amount * tuning.chainDamageMultiplier,
        damageResult.damageType,
        damageResult.isCritical,
        result,
        tower.id,
        "tower",
      );
    }
    current = next;
  }

  if (tower.type === "thunder_tower" && chainedTargets.length > 0) {
    result.events.push({
      type: "chain_lightning",
      time: 0,
      payload: {
        sourceId: tower.id,
        origin: { ...primaryTarget.position },
        targets: chainedTargets.map((enemy) => ({ ...enemy.position })),
      },
    });
  }
}

/** 对具有 regenInterval 设置的敌人施加自然生命值恢复。 */
export function applyRegen(state: GameState, dt: number, deps: CombatSystemDeps): void {
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const config = deps.resolvedMap.enemies[enemy.type];
    if (!config || !config.regenInterval || config.regenInterval <= 0) continue;
    const interval = config.regenInterval;
    enemy.regenTimer = (enemy.regenTimer ?? 0) + dt;
    while (enemy.regenTimer >= interval) {
      enemy.regenTimer -= interval;
      const heal = Math.round(enemy.maxHp * (config.regenPercentPerTick ?? 0.1));
      if (heal > 0) {
        enemy.hp = Math.min(enemy.maxHp, enemy.hp + heal);
      }
    }
  }
}

/**
 * Handle split-on-death: when an enemy dies and has `splitOnDeath`,
 * create new enemy entries at the same position with full HP.
 */
function applySplitOnDeath(state: GameState, deps: CombatSystemDeps): void {
  for (const enemy of state.enemies) {
    if (!enemy.alive || !enemy.markedDead) continue;
    const config = deps.resolvedMap.enemies[enemy.type];
    if (!config || !config.splitOnDeath || config.splitOnDeath <= 0) continue;
    const childType = config.splitInto;
    if (!childType) continue;
    const childConfig = deps.resolvedMap.enemies[childType];
    if (!childConfig) continue;
    const waveIndex = enemy.waveIndex ?? Math.max(0, state.wave - 1);
    const childStats = scaleEnemyStats(childConfig, deps.resolvedMap.map, deps.balance, waveIndex);
    const childSkills = (childConfig.skillIds ?? [])
      .map((id) => deps.skills?.[id])
      .filter((skill): skill is SkillConfig => skill?.type === "MONSTER");
    const childSkillTimers = Object.fromEntries(
      childSkills.map((skill) => {
        const condition = skill.releaseCondition;
        return [
          skill.id,
          condition?.kind === "interval" ? (condition.initialDelay ?? condition.seconds) : 0,
        ];
      }),
    );
    for (let i = 0; i < config.splitOnDeath; i++) {
      state.entityCounter += 1;
      state.enemies.push({
        id: `enemy-${state.entityCounter}`,
        type: childType,
        hp: childStats.maxHp,
        maxHp: childStats.maxHp,
        attack: childStats.attack,
        magic: childStats.magic,
        defense: childStats.defense,
        magicResistance: childStats.magicResistance,
        speed: childStats.speed,
        waveIndex,
        position: { ...enemy.position },
        waypoints: enemy.waypoints,
        currentWaypointIndex: enemy.currentWaypointIndex,
        segmentProgress: enemy.segmentProgress,
        alive: true,
        markedDead: false,
        statusEffects: [],
        skillTimers: { ...childSkillTimers },
        regenTimer: 0,
      });
    }
  }
}

/** 将所有现有投射物向其目标移动。到达时解析命中。 */
export function moveProjectiles(
  state: GameState,
  dt: number,
  result: CombatTickResult,
  deps: CombatSystemDeps,
): void {
  for (const projectile of state.projectiles) {
    if (projectile.resolved) continue;
    const target = state.enemies.find((e) => e.id === projectile.targetEnemyId);
    const targetPos = target?.position ?? null;
    if (!targetPos) {
      projectile.resolved = true;
      continue;
    }
    const dx = targetPos.x - projectile.position.x;
    const dy = targetPos.y - projectile.position.y;
    const dist = Math.hypot(dx, dy);
    const travelDist = projectile.speed * dt;
    if (projectile.speed === 0 || dist <= travelDist || dist === 0) {
      projectile.position = { x: targetPos.x, y: targetPos.y };
      projectile.resolved = true;
      if (projectile.attackMode === "area_damage" && projectile.impactAreaDamage) {
        const impact = { ...projectile.position };
        const area = projectile.impactAreaDamage;
        result.events.push({
          type: "area_damage_impact",
          time: 0,
          payload: {
            sourceId: projectile.sourceTowerId,
            position: impact,
            radius: area.radius,
            damageType: "physical",
          },
        });
        for (const enemy of state.enemies) {
          if (!enemy.alive || enemy.markedDead) continue;
          if (Math.hypot(enemy.position.x - impact.x, enemy.position.y - impact.y) > area.radius)
            continue;
          const enemyConfig = deps.resolvedMap.enemies[enemy.type];
          if (!enemyConfig) continue;
          const damageMultiplier =
            enemy.id === projectile.targetEnemyId
              ? (area.targetDamageMultiplier ?? area.damageMultiplier ?? 1)
              : (area.otherDamageMultiplier ?? area.damageMultiplier ?? 1);
          const baseDamage = area.baseAttack * damageMultiplier * area.globalDamageMultiplier;
          const mitigatedDamage =
            (baseDamage * 100) / (100 + targetView(enemy, enemyConfig).defense);
          const damage = Math.max(
            1,
            Math.floor(area.isCritical ? mitigatedDamage * area.critDamage : mitigatedDamage),
          );
          applyDamageTo(
            state,
            enemy.id,
            damage,
            "physical",
            area.isCritical,
            result,
            projectile.sourceTowerId,
            "tower",
          );
        }
      } else {
        applyDamageTo(
          state,
          projectile.targetEnemyId,
          projectile.damage,
          projectile.damageType,
          projectile.isCritical ?? false,
          result,
          projectile.sourceTowerId,
          "tower",
        );
      }
      if (projectile.attackMode === "ice_cone" && projectile.impactControl) {
        const impact = projectile.position;
        for (const enemy of state.enemies) {
          if (!enemy.alive || enemy.markedDead) continue;
          if (
            Math.hypot(enemy.position.x - impact.x, enemy.position.y - impact.y) >
            projectile.impactControl.radius
          )
            continue;
          const enemyConfig = deps.resolvedMap.enemies[enemy.type];
          if (!enemyConfig) continue;
          applyControl(
            enemy,
            enemyConfig,
            "slow",
            projectile.impactControl.controlChance,
            projectile.impactControl.sourceAccuracy,
            projectile.impactControl.slowDuration,
            projectile.impactControl.slowFactor,
            deps.random,
            result,
          );
        }
      }
    } else {
      projectile.position = {
        x: projectile.position.x + (dx / dist) * travelDist,
        y: projectile.position.y + (dy / dist) * travelDist,
      };
    }
  }
  // 压缩已解析的投射物以保持数组有界。
  state.projectiles = state.projectiles.filter((p) => !p.resolved);
}

/** 处理已标记死亡的敌人：发放金币、发出击杀事件、触发分裂效果。 */
export function processDeaths(
  state: GameState,
  result: CombatTickResult | SkillDamageResult,
  deps: CombatSystemDeps,
): void {
  const killedThisTick =
    "killedThisTick" in result && result.killedThisTick instanceof Set
      ? result.killedThisTick
      : new Set<string>();
  applySplitOnDeath(state, deps);
  for (const enemy of state.enemies) {
    if (!enemy.markedDead || !enemy.alive) continue;
    if (killedThisTick.has(enemy.id)) continue;
    killedThisTick.add(enemy.id);
    const config = deps.resolvedMap.enemies[enemy.type];
    if (!config) continue;
    const goldReward = calculateMonsterGoldReward(
      config.goldReward,
      deps.balance.goldRewardMultiplier ?? 1,
    );
    state.gold += goldReward;
    result.killEvents.push({
      enemyId: enemy.id,
      enemyType: enemy.type,
      goldReward,
      isBoss: config.isBoss === true,
    });
    enemy.alive = false;
  }
}

export interface CombatTickResult {
  damageEvents: Array<{
    enemyId: string;
    damage: number;
    damageType: DamageType;
    isCritical: boolean;
    hpRemaining: number;
    sourceId?: string;
    sourceKind?: "tower" | "enemy" | "skill" | "environment";
  }>;
  killEvents: Array<{
    enemyId: string;
    enemyType: string;
    goldReward: number;
    isBoss: boolean;
  }>;
  newProjectiles: import("../types").ProjectileState[];
  /** 本 Tick 落地的轨道打击；伤害已参与死亡与奖励处理。 */
  landedOrbitalStrikes: Array<{ id: string; position: Point; damage?: number }>;
  /** 战斗管线事件（§34），由引擎盖章并发出 */
  events: Array<import("../types").GameEvent>;
  killedThisTick: Set<string>;
}

export interface SkillDamageResult {
  damageEvents: Array<{
    enemyId: string;
    damage: number;
    damageType: DamageType;
    isCritical: boolean;
    hpRemaining: number;
    sourceId?: string;
    sourceKind?: "tower" | "enemy" | "skill" | "environment";
  }>;
  killEvents: Array<{
    enemyId: string;
    enemyType: string;
    goldReward: number;
    isBoss: boolean;
  }>;
  events: GameEvent[];
  /** true 时将死亡/金币奖励留给当前 CombatTickResult 的统一阶段处理。 */
  deferDeathProcessing?: boolean;
}

/**
 * 对当前游戏状态应用技能效果。
 */
export function applySkillDamage(
  state: GameState,
  skillConfig: SkillConfig,
  position: Point,
  deps: CombatSystemDeps,
  options: { deferDeathProcessing?: boolean; damageOverride?: number } = {},
): SkillDamageResult {
  const result: SkillDamageResult = {
    damageEvents: [],
    killEvents: [],
    events: [],
  };

  // --- area_damage ---
  if (skillConfig.damage != null && skillConfig.damage > 0 && skillConfig.radius != null) {
    const damageType = skillConfig.damageType ?? "physical";
    for (const enemy of state.enemies) {
      if (!enemy.alive) continue;
      const dist = Math.hypot(enemy.position.x - position.x, enemy.position.y - position.y);
      if (dist > skillConfig.radius) continue;
      const enemyConfig = deps.resolvedMap.enemies[enemy.type];
      if (!enemyConfig) continue;
      const dmgResult = calculateDamage(
        {
          id: "skill",
          cost: 0,
          damageType,
          projectileSpeed: 0,
          maxLevel: 1,
          sellRefundRate: 0,
          stats: {
            maxHp: 0,
            defense: 0,
            magicResistance: 0,
            accuracy: 0,
            resistance: 0,
            attack: options.damageOverride ?? skillConfig.damage ?? 0,
            magic: options.damageOverride ?? skillConfig.damage ?? 0,
            attackSpeed: 1,
            speed: 0,
            critRate: 0,
            critDamage: 1.5,
          },
          upgrades: [{ level: 1 }],
        } as import("@bastion/data").TowerConfig,
        1,
        targetView(enemy, deps.resolvedMap.enemies[enemy.type]),
        { balance: deps.balance },
      );
      applyDamageTo(
        state,
        enemy.id,
        dmgResult.amount,
        dmgResult.damageType,
        dmgResult.isCritical,
        result as CombatTickResult,
        undefined,
        "skill",
      );
    }
    // 由调用端延迟处理时，死亡归属和金币奖励统一留在本 Tick 的 Death / Reward 阶段。
    if (!options.deferDeathProcessing) {
      processDeaths(state, result as CombatTickResult, deps);
    }
  }

  // --- legacy skill slow ---
  if (
    skillConfig.slowFactor != null &&
    skillConfig.slowFactor > 0 &&
    skillConfig.slowDuration != null
  ) {
    for (const enemy of state.enemies) {
      if (!enemy.alive) continue;
      applySlowTo(enemy, skillConfig.slowFactor, skillConfig.slowDuration);
    }
  }

  // --- heal_base_hp ---
  if (skillConfig.healLives != null && skillConfig.healLives > 0) {
    if (state.status !== "lost") {
      state.baseHp = Math.min(state.maxHp, state.baseHp + Math.round(skillConfig.healLives));
    }
  }

  // --- 塔攻速加成：对施放技能的塔自身施加攻击速度提升（规则 §32 扩展）。 ---
  if (
    skillConfig.attackSpeedFactor != null &&
    skillConfig.attackSpeedFactor > 1 &&
    skillConfig.attackSpeedDuration != null
  ) {
    // 找到施放技能的塔（通过位置匹配）
    const firingTower = state.towers.find(
      (t) => t.position.x === position.x && t.position.y === position.y,
    );
    if (firingTower) {
      firingTower.attackSpeedBoosts.push({
        factor: skillConfig.attackSpeedFactor,
        remaining: skillConfig.attackSpeedDuration,
      });
    }
  }

  return result;
}
